'use client'

import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { ArrowRight, Sparkles, Rocket, MessageSquarePlus } from 'lucide-react'
import Link from 'next/link'

export default function ChangelogPage() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  }

  const releases = [
    {
      version: 'v1.4',
      date: 'February 2026',
      type: 'Update',
      changes: [
        'Custom bid creation for opportunities outside SAM.gov',
        'Enhanced user profiles',
        'Usage alerts and notifications',
      ]
    },
    {
      version: 'v1.3',
      date: 'February 2026',
      type: 'Feature Release',
      changes: [
        'AI Coaching with GovCon Readiness Score (Professional)',
        'Deep Research mode for live government contracting intel',
        'Persistent coaching memory across sessions',
      ]
    },
    {
      version: 'v1.2',
      date: 'February 2026',
      type: 'Update',
      changes: [
        'AI-powered opportunity matching',
        'Improved search and discovery',
      ]
    },
    {
      version: 'v1.1',
      date: 'February 2026',
      type: 'Feature Release',
      changes: [
        '14-day free trial with full access',
        'Bid Management with tracking, documents, and AI checklists',
        'AI Proposal Generator',
        'AI Insights for research and analysis',
        'GovBot AI assistant (Standard+)',
      ]
    },
    {
      version: 'v1.0',
      date: 'November 2025',
      type: 'Initial Launch',
      changes: [
        'Core dashboard and authentication',
        'Subscription tiers (Free, Standard, Professional)',
        'Federal and state opportunity browsing',
        'Analytics and reporting',
        'Security and compliance features',
      ]
    }
  ]

  const upcoming = [
    {
      title: 'Collaboration Suite',
      description: 'Enhanced team collaboration tools with real-time editing and workflow management.',
      eta: 'Q1 2026'
    },
    {
      title: 'Interactive Contract Mapping',
      description: 'Visualize contract locations, performance areas, and related opportunities geographically.',
      eta: 'Q2 2026'
    },
    {
      title: 'Enhanced Predictive Analytics',
      description: 'Improved ML models for win probability, pricing strategy, and competitor insights.',
      eta: 'Q3 2026'
    },
    {
      title: 'Autonomous AI Agents',
      description: 'Deploy specialized AI agents to automate tasks like opportunity scouting, compliance monitoring, and reporting.',
      eta: 'Q4 2026'
    }
  ]

  return (
    <div className="container max-w-4xl mx-auto px-4 py-16">
      {/* Header */}
      <motion.div
        className="text-center mb-16"
        initial="initial"
        animate="animate"
        variants={fadeIn}
      >
        <h1 className="text-4xl font-bold mb-4">Product Updates</h1>
        <p className="text-xl text-muted-foreground">
          Track our latest features and see what's coming next
        </p>
      </motion.div>

      {/* Feature Request Button */}
      <motion.div
        className="mb-16 text-center"
        initial="initial"
        animate="animate"
        variants={fadeIn}
      >
        <Link href="/contact">
          <Button size="lg" className="gap-2">
            <MessageSquarePlus className="h-5 w-5" />
            Submit Feature Request
          </Button>
        </Link>
      </motion.div>

      {/* Current Releases */}
      <motion.div
        className="mb-16"
        initial="initial"
        animate="animate"
        variants={fadeIn}
      >
        <div className="flex items-center gap-2 mb-8">
          <Rocket className="h-6 w-6 text-primary" />
          <h2 className="text-2xl font-bold">Current Releases</h2>
        </div>
        <div className="space-y-12">
          {releases.map((release) => (
            <div
              key={release.version}
              className="border rounded-lg p-6 space-y-4 bg-card"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-semibold">{release.version}</h3>
                  <p className="text-sm text-muted-foreground">{release.date}</p>
                </div>
                <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                  {release.type}
                </span>
              </div>
              <ul className="space-y-2">
                {release.changes.map((change, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>{change}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Upcoming Features */}
      <motion.div
        initial="initial"
        animate="animate"
        variants={fadeIn}
      >
        <div className="flex items-center gap-2 mb-8">
          <Sparkles className="h-6 w-6 text-primary" />
          <h2 className="text-2xl font-bold">Future Roadmap</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {upcoming.map((feature, index) => (
            <div
              key={index}
              className="border rounded-lg p-6 space-y-3 bg-card"
            >
              <h3 className="text-lg font-semibold">{feature.title}</h3>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Expected:</span>
                <span className="text-sm text-muted-foreground">{feature.eta}</span>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  )
} 